/**
 * Reusable component for rendering cards grid
 *
 * Copyright (c) 2023 Propellant Software Corp
 * Author: Yucho Ho
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { Row, Col } from 'reactstrap'
import LoadButton from 'client/shared/js/components/LoadButton'
import LoadingScreen from 'client/shared/js/components/LoadingScreen'
import EmptyStates from 'client/shared/js/components/EmptyStates/EmptyStates'
import styles from './CardsGrid.module.scss'

const DENSITIES = {
  default: {
    sm:'12', md:'6', lg:'6', xl:'4'
  },
  'tall-cards': {
    sm:'12', md:'6', lg:'6', xl:'3'
  }
}

const CardsGrid = ({
  cards,
  onLoadMore,
  isLoading,
  shouldLoadMore,
  loadMoreMessage,
  emptyImage,
  emptyMessage,
  density,
  loader
}) => {
  const Cards = useMemo(() => cards.map((card) => {
    const { id, component } = card
    const colDensityProps = DENSITIES[density]

    return (
      <Col key={id} {...colDensityProps} className={cx(styles.card)}>
        {component}
      </Col>
    )
  }), [cards, density])

  const LoadMoreButton = useMemo(() => (
    (shouldLoadMore || isLoading) ? (
      <div className={cx(styles['load-more-container'])}>
        <LoadButton isLoading={isLoading} onClick={onLoadMore}>
          {loadMoreMessage}
        </LoadButton>
      </div>
    ) : null
  ), [onLoadMore, shouldLoadMore, isLoading, loadMoreMessage])

  if (isLoading) {
    return <LoadingScreen loader={loader} />
  }

  const isEmpty = !cards || cards.length === 0
  if (isEmpty) {
    if (emptyMessage || emptyImage) {
      return <EmptyStates image={emptyImage} text={emptyMessage} />
    }

    return null
  }

  return (
    <>
      <Row className={cx(styles['cards-grid'])}>
        {Cards}
      </Row>
      {LoadMoreButton}
    </>
  )
}

CardsGrid.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.object),
  onLoadMore: PropTypes.func,
  isLoading: PropTypes.bool,
  shouldLoadMore: PropTypes.bool,
  loadMoreMessage: PropTypes.string,
  emptyImage: PropTypes.node,
  emptyMessage: PropTypes.string,
  density: PropTypes.oneOf(Object.keys(DENSITIES)),
  loader: PropTypes.string
}

CardsGrid.defaultProps = {
  loadMoreMessage: 'Load more',
  emptyMessage: "There's nothing here yet. Check back soon!",
  density: 'default'
}

export default CardsGrid
